
// Sod's Law Calculator view integrated with site styles (no inline CSS in HTML)
// Reuses the original formula and interaction, scoped under .calc- classes

import templateHtml from '@views/templates/sods-calculator.html?raw';
import { SOCIAL_IMAGE_SOD, SITE_NAME } from '@utils/constants.ts';
import { renderSodsFormula } from '@utils/mathjax.ts';
import { hydrateIcons } from '@utils/icons.ts';
import { updatePageMetadata } from '@utils/dom.ts';
import { setExportContent, clearExportContent, ContentType } from '@utils/export-context.ts';
import { renderInlineShareButtonsHTML, initInlineShareButtons } from '@components/social-share.ts';
import { trackProductEvent } from '@utils/metrics.ts';
import { parseCalculatorState, serializeCalculatorState } from '@utils/calculator-state.ts';
import { getCalculatorScenarioLinks, renderInternalLinkList } from '@utils/internal-links.ts';
import type { CleanableElement } from '../types/app.d.ts';

type SliderKey = 'urgency' | 'complexity' | 'importance' | 'skill' | 'frequency';

export function Calculator(): HTMLDivElement {
  const el = document.createElement('div');
  el.className = 'container page calculator';

  el.innerHTML = templateHtml;

  // Hydrate icons
  hydrateIcons(el);

  /* v8 ignore next -- SSR guard: document is always defined in browser/jsdom */
  if (typeof document !== 'undefined') {
    updatePageMetadata({
      title: `Sod's Law Calculator | ${SITE_NAME}`,
      description: "Explore a playful task-risk heuristic using urgency, complexity, importance, skill, and frequency. For entertainment and lightweight planning only.",
      path: '/calculator/sods-law',
      image: SOCIAL_IMAGE_SOD
    });
  }

  // Wire up interactions
  const _sliders: Record<SliderKey, HTMLInputElement | null> = {
    urgency: el.querySelector<HTMLInputElement>('#urgency'),
    complexity: el.querySelector<HTMLInputElement>('#complexity'),
    importance: el.querySelector<HTMLInputElement>('#importance'),
    skill: el.querySelector<HTMLInputElement>('#skill'),
    frequency: el.querySelector<HTMLInputElement>('#frequency'),
  };

  // Verify all sliders exist
  for (const [name, slider] of Object.entries(_sliders)) {
    /* v8 ignore next -- slider is always provided by the template HTML */
    if (!slider) throw new Error(`Calculator slider "${name}" not found`);
  }
  const sliders = _sliders as Record<SliderKey, HTMLInputElement>;

  const sliderValues = {
    urgency: el.querySelector('#urgency-value')!,
    complexity: el.querySelector('#complexity-value')!,
    importance: el.querySelector('#importance-value')!,
    skill: el.querySelector('#skill-value')!,
    frequency: el.querySelector('#frequency-value')!,
  };

  const scoreValueDisplay = el.querySelector('#score-value')!;
  const scoreInterpretationDisplay = el.querySelector('#score-interpretation')!;
  const resultDisplay = el.querySelector('#result-display')!;
  const formulaDisplay = el.querySelector('#formula-display')!;

  // Track which variables should show values (temporarily after slider change)
  type FormulaVarKey = 'U' | 'C' | 'I' | 'S' | 'F';
  const showValues: Record<FormulaVarKey, boolean> = { U: false, C: false, I: false, S: false, F: false };
  let resetTimeouts: Record<FormulaVarKey, ReturnType<typeof setTimeout> | null> = { U: null, C: null, I: null, S: null, F: null };
  let hasTrackedStart = false;

  function clearFormulaTimeouts() {
    new Set(Object.values(resetTimeouts)).forEach(timeout => {
      if (timeout) clearTimeout(timeout);
    });
    resetTimeouts = { U: null, C: null, I: null, S: null, F: null };
  }

  function updateCalculation() {
    const U = parseFloat(sliders.urgency.value);
    const C = parseFloat(sliders.complexity.value);
    const I = parseFloat(sliders.importance.value);
    const S = parseFloat(sliders.skill.value);
    const F = parseFloat(sliders.frequency.value);
    const A = 0.7;

    // Calculate score (scale 0-8.6 per FAQ; display as 0-100% for UX consistency with Buttered Toast)
    const score = ((U + C + I) * (10 - S)) / 20 * A * (1 / (1 - Math.sin(F / 10)));
    const displayScore = Math.min(score, 8.6);
    const displayPercent = Math.round((displayScore / 8.6) * 100);

    scoreValueDisplay.textContent = `${displayPercent}%`;
    updateResultInterpretation(displayScore);

    /* v8 ignore next -- textContent is always set by updateResultInterpretation() */
    const interpretation = scoreInterpretationDisplay.textContent || '';
    setExportContent({
      type: ContentType.CONTENT,
      title: "Sod's Law Calculator",
      data: `Probability of things going wrong: ${displayPercent}%. ${interpretation}`
    });

    const pFormula = showValues.U ? String(displayPercent) : 'P';
    const uDisplay = showValues.U ? U : 'U';
    const cDisplay = showValues.C ? C : 'C';
    const iDisplay = showValues.I ? I : 'I';
    const sDisplay = showValues.S ? S : 'S';
    const fDisplay = showValues.F ? F : 'F';
    const aDisplay = showValues.U ? '0.7' : 'A'; // Show 0.7 when flashing
    renderSodsFormula(formulaDisplay, {
      probability: pFormula,
      urgency: uDisplay,
      complexity: cDisplay,
      importance: iDisplay,
      skill: sDisplay,
      frequency: fDisplay,
      activity: aDisplay,
    });
  }

  function flashAllVariables() {
    // Show all values temporarily
    (Object.keys(showValues) as FormulaVarKey[]).forEach(v => showValues[v] = true);
    updateCalculation();

    // Clear existing timeouts
    clearFormulaTimeouts();

    // Reset all back to variable names after 2 seconds
    const timeout = setTimeout(() => {
      if (!el.isConnected) return;
      (Object.keys(showValues) as FormulaVarKey[]).forEach(v => showValues[v] = false);
      updateCalculation();
    }, 2000);

    // Store timeout for all variables
    (Object.keys(resetTimeouts) as FormulaVarKey[]).forEach(v => resetTimeouts[v] = timeout);
  }

  (Object.keys(sliders) as SliderKey[]).forEach((k) => {
    sliders[k].addEventListener('input', () => {
      if (!hasTrackedStart) {
        hasTrackedStart = true;
        trackProductEvent('calculator.start', { surface: 'calculator_page', calculator: 'sods-law' });
      }
      const val = sliders[k].value;
      sliderValues[k].textContent = val;

      // Update ARIA attributes
      sliders[k].setAttribute('aria-valuenow', val);
      sliders[k].setAttribute('aria-valuetext', val);

      flashAllVariables();
      trackProductEvent('calculator.complete', { surface: 'calculator_page', calculator: 'sods-law' });
    });
  });

  updateCalculation();

  function updateResultInterpretation(score: number) {
    let interpretation: string;
    let cls: string;

    if (score < 2) {
      interpretation = "You're probably safe. What could possibly go wrong?";
      cls = 'calc-ok';
    } else if (score < 4) {
      interpretation = 'A bit risky. Maybe have a backup plan.';
      cls = 'calc-warn';
    } else if (score < 6) {
      interpretation = 'Definitely worrying. Proceed with caution.';
      cls = 'calc-orange';
    } else if (score < 8) {
      interpretation = "Disaster is looming. It's not looking good.";
      cls = 'calc-danger';
    } else {
      interpretation = 'Catastrophe is almost certain. Good luck.';
      cls = 'calc-dark';
    }

    scoreInterpretationDisplay.textContent = interpretation;
    resultDisplay.classList.remove('calc-ok', 'calc-warn', 'calc-orange', 'calc-danger', 'calc-dark');
    resultDisplay.classList.add(cls);
  }

  const state = {
    urgency: parseFloat(sliders.urgency.value),
    complexity: parseFloat(sliders.complexity.value),
    importance: parseFloat(sliders.importance.value),
    skill: parseFloat(sliders.skill.value),
    frequency: parseFloat(sliders.frequency.value),
    /* v8 ignore next -- textContent is always set by updateCalculation() before this runs */
    probability: scoreValueDisplay.textContent || '0%',
    /* v8 ignore next -- textContent is always set by updateCalculation() before this runs */
    interpretation: scoreInterpretationDisplay.textContent || ''
  };

  function updateState() {
    state.urgency = parseFloat(sliders.urgency.value);
    state.complexity = parseFloat(sliders.complexity.value);
    state.importance = parseFloat(sliders.importance.value);
    state.skill = parseFloat(sliders.skill.value);
    state.frequency = parseFloat(sliders.frequency.value);
    /* v8 ignore next -- textContent is always set by updateCalculation() before this runs */
    state.probability = scoreValueDisplay.textContent || '0%';
    /* v8 ignore next -- textContent is always set by updateCalculation() before this runs */
    state.interpretation = scoreInterpretationDisplay.textContent || '';
  }

  // Generate shareable URL with parameters
  function getShareableUrl() {
    updateState();
    return serializeCalculatorState(window.location.href, {
      u: state.urgency,
      c: state.complexity,
      i: state.importance,
      s: state.skill,
      f: state.frequency,
    });
  }

  // Generate share text for social platforms
  function getShareText() {
    updateState();
    return `My Sod's Law score is ${state.probability}! ${state.interpretation}`;
  }

  // Load parameters from URL if present
  const parsedState = parseCalculatorState(new URLSearchParams(window.location.search), {
    u: { min: 1, max: 9 },
    c: { min: 1, max: 9 },
    i: { min: 1, max: 9 },
    s: { min: 1, max: 9 },
    f: { min: 1, max: 9 },
  });
  const paramKeys: Record<string, SliderKey> = { u: 'urgency', c: 'complexity', i: 'importance', s: 'skill', f: 'frequency' };

  Object.entries(parsedState).forEach(([param, value]) => {
    const slider = paramKeys[param];
    if (slider) {
      sliders[slider].value = String(value);
      sliderValues[slider].textContent = String(value);
    }
  });

  const scenarioLinks = el.querySelector('[data-calculator-scenario-links]');
  if (scenarioLinks) {
    scenarioLinks.innerHTML = renderInternalLinkList(getCalculatorScenarioLinks('sods-law'));
  }

  const shareContainer = el.querySelector('#calculator-share-container')!;
  shareContainer.innerHTML = renderInlineShareButtonsHTML();
  hydrateIcons(shareContainer);

  // Initialize inline share buttons
  const teardownShare = initInlineShareButtons(el, {
    getShareableUrl,
    getShareText,
    emailSubject: "Check out my Sod's Law calculation"
  });

  (el as CleanableElement).cleanup = () => {
    clearFormulaTimeouts();
    clearExportContent();
    teardownShare();
  };

  return el;
}
